import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SessionManager } from '@/utils/sessionManager';
import {
  User,
  Mail,
  Phone,
  CreditCard,
  Camera,
  Save,
  ArrowLeft,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { getApiBaseUrl } from '@/config/production';
import { apiClient } from '@/services/api-client';
import { BankInfo } from '@/types/photographer';

interface UserProfile {
  id: string;
  email: string;
  name?: string;
  fullName?: string;
  avatar_url?: string;
  cpf?: string;
  phone?: string;
  displayName?: string;
  role?: 'admin' | 'photographer' | 'athlete';
  status?: 'Active' | 'PendingApproval' | 'Rejected' | 'Suspended';
  created_at: string;
  updated_at: string;
  email_confirmed_at?: string;
  last_sign_in_at?: string;
}

export default function ProfilePage() {
  const [, setLocation] = useLocation();
  const { user, logout } = useUser();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    cpf: '',
    phone: '',
    displayName: '',
    // Campos de faturamento
    bank_name: '',
    agency: '',
    account_number: '',
    account_type: 'checking' as 'checking' | 'savings',
    pix_key: '',
    pix_type: 'email' as 'cpf' | 'cnpj' | 'email' | 'phone' | 'random'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [bankInfo, setBankInfo] = useState<BankInfo | null>(null);

  const API_BASE = getApiBaseUrl();

  useEffect(() => {
    if (!user) {
      setLocation('/login');
      return;
    }
    loadProfile();
    loadBankInfo();
  }, [user, setLocation]);

  const loadProfile = async () => {
    try {
      const token = SessionManager.getToken();
      if (!token) {
        setLocation('/login');
        return;
      }

      const response = await fetch(`${API_BASE}/api/users/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // O backend pode retornar diretamente o objeto User ou um objeto com success/data
        let userProfile = data;
        
        // Se retornar formato { success: false, message, data: null }
        if (data && typeof data === 'object' && 'success' in data) {
          if (!data.success) {
            setError(data.message || 'Dados do perfil não encontrados');
            return;
          }
          userProfile = data.data;
        }

        if (!userProfile || !userProfile.id) {
          setError('Dados do perfil não encontrados');
          return;
        }

        // Converter para o formato esperado pelo componente
        // O backend retorna User com createdAt/updatedAt como Date, precisamos converter
        const profileData: UserProfile = {
          id: userProfile.id,
          email: userProfile.email || '',
          name: userProfile.fullName || userProfile.name,
          fullName: userProfile.fullName || userProfile.name,
          cpf: userProfile.cpf,
          phone: userProfile.phone,
          displayName: userProfile.displayName,
          role: userProfile.role,
          status: userProfile.status,
          created_at: userProfile.createdAt instanceof Date 
            ? userProfile.createdAt.toISOString() 
            : typeof userProfile.createdAt === 'string' 
            ? userProfile.createdAt 
            : new Date().toISOString(),
          updated_at: userProfile.updatedAt instanceof Date 
            ? userProfile.updatedAt.toISOString() 
            : typeof userProfile.updatedAt === 'string' 
            ? userProfile.updatedAt 
            : new Date().toISOString(),
        };

        setProfile(profileData);
        setFormData(prev => ({
          ...prev,
          fullName: profileData.fullName || profileData.name || '',
          cpf: profileData.cpf || '',
          phone: profileData.phone || '',
          displayName: profileData.displayName || ''
        }));
      } else if (response.status === 401) {
        // Token expirado, tentar renovar
        const refreshSuccess = await tryRefreshToken();
        if (refreshSuccess) {
          // Tentar novamente com o novo token
          await loadProfile();
        } else {
          // Não foi possível renovar, redirecionar para login
          setLocation('/login');
        }
      } else {
        setError('Erro ao carregar perfil');
      }
    } catch (error) {
      console.error('Erro ao carregar perfil:', error);
      setError('Erro ao carregar perfil');
    } finally {
      setIsLoading(false);
    }
  };

  const loadBankInfo = async () => {
    try {
      // Só carregar dados bancários se o usuário for fotógrafo
      if (user?.role === 'photographer') {
        const { data } = await apiClient.get('/photographer/bank-info');
        if (data?.success && data?.data) {
          setBankInfo(data.data);
          // Preencher formData com dados bancários
          setFormData(prev => ({
            ...prev,
            bank_name: data.data.bank_name || '',
            agency: data.data.agency || '',
            account_number: data.data.account_number || '',
            account_type: (data.data.account_type || 'checking') as 'checking' | 'savings',
            pix_key: data.data.pix_key || '',
            pix_type: (data.data.pix_type || 'email') as 'cpf' | 'cnpj' | 'email' | 'phone' | 'random'
          }));
        } else {
          // Se não houver dados bancários, manter valores padrão
          setBankInfo(null);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados bancários:', error);
      // Não mostrar erro se não for fotógrafo ou se não houver dados ainda
      setBankInfo(null);
    }
  };

  const tryRefreshToken = async (): Promise<boolean> => {
    try {
      const refreshToken = localStorage.getItem('fotup_refresh_token');
      if (!refreshToken) {
        return false;
      }

      const response = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh_token: refreshToken })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.session) {
          SessionManager.saveTokensDirectly(data.session.access_token, data.session.refresh_token);
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Erro ao renovar token:', error);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = SessionManager.getToken();
      if (!token) {
        setLocation('/login');
        return;
      }

      // Separar dados pessoais e bancários
      const { bank_name, agency, account_number, account_type, pix_key, pix_type, ...personalData } = formData;

      // Atualizar dados pessoais
      const response = await fetch(`${API_BASE}/api/users/profile`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(personalData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Erro ao atualizar perfil');
      }

      const data = await response.json();
      // O backend pode retornar diretamente o objeto ou dentro de data
      const updatedProfile = data.data || data;

      // Se for fotógrafo, atualizar dados bancários também
      if (user?.role === 'photographer') {
        try {
          await apiClient.put('/photographer/bank-info', {
            bank_name,
            agency,
            account_number,
            account_type,
            pix_key,
            pix_type
          });
        } catch (bankError: any) {
          console.error('Erro ao atualizar dados bancários:', bankError);
          // Não falhar o submit se houver erro nos dados bancários, apenas avisar
          setError('Perfil atualizado, mas houve erro ao atualizar dados bancários');
          await loadProfile();
          await loadBankInfo();
          setIsSaving(false);
          return;
        }
      }

      setSuccess('Perfil atualizado com sucesso!');
      await loadProfile(); // Recarregar dados atualizados
      await loadBankInfo(); // Recarregar dados bancários
    } catch (error: any) {
      console.error('Erro ao atualizar perfil:', error);
      setError(error.message || 'Erro ao atualizar perfil');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    } else {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
  };

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setFormData(prev => ({
      ...prev,
      cpf: formatted
    }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setFormData(prev => ({
      ...prev,
      phone: formatted
    }));
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'Active':
        return <Badge className="bg-primary/10 text-primary"><CheckCircle className="w-3 h-3 mr-1" />Ativo</Badge>;
      case 'PendingApproval':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Aguardando Aprovação</Badge>;
      case 'Rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Rejeitado</Badge>;
      case 'Suspended':
        return <Badge className="bg-gray-100 text-gray-800"><XCircle className="w-3 h-3 mr-1" />Suspenso</Badge>;
      default:
        return <Badge variant="secondary">Desconhecido</Badge>;
    }
  };

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'photographer':
        return 'Fotógrafo';
      case 'athlete':
        return 'Atleta';
      default:
        return 'Usuário';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando perfil...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Erro ao carregar perfil</h2>
          <p className="text-gray-600 mb-4">Não foi possível carregar as informações do seu perfil.</p>
          <Button onClick={() => setLocation('/')}>Voltar ao início</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => setLocation('/')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao início
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Meu Perfil</h1>
          <p className="text-gray-600 mt-2">
            Gerencie suas informações pessoais e configurações da conta
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Informações da conta */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Informações da Conta
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-gray-500">Email</Label>
                  <div className="flex items-center mt-1">
                    <Mail className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm">{profile.email}</span>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-500">Tipo de conta</Label>
                  <div className="mt-1">
                    <Badge variant="outline">{getRoleLabel(profile.role)}</Badge>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-500">Status</Label>
                  <div className="mt-1">
                    {getStatusBadge(profile.status)}
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-500">Membro desde</Label>
                  <p className="text-sm text-gray-600 mt-1">
                    {new Date(profile.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>

                {profile.last_sign_in_at && (
                  <div>
                    <Label className="text-sm font-medium text-gray-500">Último acesso</Label>
                    <p className="text-sm text-gray-600 mt-1">
                      {new Date(profile.last_sign_in_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                )}

                <div className="pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      logout();
                      setLocation('/');
                    }}
                    className="w-full"
                  >
                    Sair da conta
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Card de Dados de Faturamento */}
            {user?.role === 'photographer' && bankInfo && (bankInfo.bank_name || bankInfo.agency || bankInfo.account_number || bankInfo.pix_key) && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <CreditCard className="w-5 h-5 mr-2" />
                    Faturamento
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {bankInfo.bank_name && bankInfo.agency && bankInfo.account_number && (
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Dados Bancários</Label>
                      <div className="mt-1">
                        <p className="text-sm text-gray-700">
                          {bankInfo.bank_name} - Ag: {bankInfo.agency} - Conta: {bankInfo.account_number}
                        </p>
                      </div>
                    </div>
                  )}
                  {bankInfo.pix_key && (
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Chave PIX</Label>
                      <div className="mt-1">
                        <p className="text-sm text-gray-700">{bankInfo.pix_key}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Formulário de edição */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Informações Pessoais</CardTitle>
                <CardDescription>
                  Atualize suas informações pessoais abaixo
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  {success && (
                    <Alert className="border-primary/20 bg-primary/10 text-primary">
                      <CheckCircle className="h-4 w-4" />
                      <AlertDescription>{success}</AlertDescription>
                    </Alert>
                  )}

                  {/* Nome completo */}
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Nome completo</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="fullName"
                        name="fullName"
                        type="text"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="pl-10"
                        placeholder="Seu nome completo"
                        required
                      />
                    </div>
                  </div>

                  {/* Nome de exibição */}
                  <div className="space-y-2">
                    <Label htmlFor="displayName">Nome de exibição</Label>
                    <div className="relative">
                      <Camera className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="displayName"
                        name="displayName"
                        type="text"
                        value={formData.displayName}
                        onChange={handleInputChange}
                        className="pl-10"
                        placeholder="Nome que aparece nas fotos"
                      />
                    </div>
                    <p className="text-sm text-gray-500">
                      Este nome aparecerá nas fotos que você comprar ou baixar
                    </p>
                  </div>

                  {/* CPF */}
                  <div className="space-y-2">
                    <Label htmlFor="cpf">CPF</Label>
                    <div className="relative">
                      <CreditCard className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="cpf"
                        name="cpf"
                        type="text"
                        value={formData.cpf}
                        onChange={handleCPFChange}
                        className="pl-10"
                        placeholder="000.000.000-00"
                        maxLength={14}
                      />
                    </div>
                  </div>

                  {/* Telefone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="phone"
                        name="phone"
                        type="text"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        className="pl-10"
                        placeholder="(11) 99999-9999"
                        maxLength={15}
                      />
                    </div>
                  </div>

                  {/* Campos de Faturamento - Apenas para fotógrafos */}
                  {user?.role === 'photographer' && (
                    <>
                      <div className="pt-6 border-t">
                        <div className="flex items-center mb-4">
                          <CreditCard className="w-5 h-5 mr-2 text-primary" />
                          <h3 className="text-lg font-semibold">Faturamento</h3>
                        </div>
                        <p className="text-sm text-gray-500 mb-4">
                          Configure seus dados bancários para receber pagamentos
                        </p>
                      </div>

                      {/* Banco */}
                      <div className="space-y-2">
                        <Label htmlFor="bank_name">Banco</Label>
                        <div className="relative">
                          <CreditCard className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            id="bank_name"
                            name="bank_name"
                            type="text"
                            value={formData.bank_name}
                            onChange={handleInputChange}
                            className="pl-10"
                            placeholder="Ex: Banco do Brasil"
                          />
                        </div>
                      </div>

                      {/* Agência e Conta */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="agency">Agência</Label>
                          <Input
                            id="agency"
                            name="agency"
                            type="text"
                            value={formData.agency}
                            onChange={handleInputChange}
                            placeholder="Ex: 1234-5"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="account_number">Conta</Label>
                          <Input
                            id="account_number"
                            name="account_number"
                            type="text"
                            value={formData.account_number}
                            onChange={handleInputChange}
                            placeholder="Ex: 12345-6"
                          />
                        </div>
                      </div>

                      {/* Tipo de Conta */}
                      <div className="space-y-2">
                        <Label htmlFor="account_type">Tipo de Conta</Label>
                        <Select
                          value={formData.account_type}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, account_type: value as 'checking' | 'savings' }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o tipo de conta" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="checking">Conta Corrente</SelectItem>
                            <SelectItem value="savings">Conta Poupança</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Chave PIX */}
                      <div className="space-y-2">
                        <Label htmlFor="pix_key">Chave PIX</Label>
                        <Input
                          id="pix_key"
                          name="pix_key"
                          type="text"
                          value={formData.pix_key}
                          onChange={handleInputChange}
                          placeholder="Ex: maria.fotografa@fotup.com"
                        />
                      </div>

                      {/* Tipo de Chave PIX */}
                      <div className="space-y-2">
                        <Label htmlFor="pix_type">Tipo de Chave PIX</Label>
                        <Select
                          value={formData.pix_type}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, pix_type: value as 'cpf' | 'cnpj' | 'email' | 'phone' | 'random' }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o tipo de chave PIX" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="email">E-mail</SelectItem>
                            <SelectItem value="cpf">CPF</SelectItem>
                            <SelectItem value="cnpj">CNPJ</SelectItem>
                            <SelectItem value="phone">Telefone</SelectItem>
                            <SelectItem value="random">Chave Aleatória</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  <div className="flex justify-end space-x-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setLocation('/')}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Salvando...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Salvar alterações
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
